import { useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Loader2, Pencil, Plus, RotateCcw, Trash2, Upload, Info } from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useOrgSettings, OrgSettingsUpdate } from '../../hooks/useOrgSettings';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { AddressSearch, SelectedPlace } from '../../components/ui/AddressSearch';
import { PhoneInput, parseStoredPhone, phoneToE164 } from '../../components/ui/PhoneInput';
import type { CountryCode } from 'libphonenumber-js';
import { api } from '../../lib/api';
import type { StateLicense, Organization } from '../../types/admin';

// ── Constants ──────────────────────────────────────────────────────────────

const DEFAULT_BRAND_PRIMARY = '#6B2D8B';
const DEFAULT_BRAND_TEXT = '#FFFFFF';

// ── Color utilities (same as OnlineLeadsSettingsPage) ──────────────────────

function isValidHex(hex: string): boolean {
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex);
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
    hex.length === 4 ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex,
  );
  if (!result) return null;
  return { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) };
}

function relativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map(c => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

function bestContrastColor(bgHex: string): string {
  const rgb = hexToRgb(bgHex);
  if (!rgb) return '#FFFFFF';
  const lum = relativeLuminance(rgb.r, rgb.g, rgb.b);
  return lum > 0.179 ? '#000000' : '#FFFFFF';
}

function contrastRatio(hex1: string, hex2: string): number {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);
  if (!rgb1 || !rgb2) return 1;
  const l1 = relativeLuminance(rgb1.r, rgb1.g, rgb1.b);
  const l2 = relativeLuminance(rgb2.r, rgb2.g, rgb2.b);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

// ── Shared sub-components ──────────────────────────────────────────────────

function CardEditButton({ editing, onEdit, onSave, onCancel, saving }: {
  editing: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  saving?: boolean;
}) {
  if (!editing) {
    return (
      <button
        type="button"
        onClick={onEdit}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-plum border border-plum rounded-lg hover:bg-plum/5 transition-colors"
      >
        <Pencil size={12} />
        Edit
      </button>
    );
  }
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-plum text-white rounded-lg hover:bg-plum/90 disabled:opacity-50 transition-colors"
      >
        {saving && <Loader2 size={12} className="animate-spin" />}
        Save
      </button>
      <button
        type="button"
        onClick={onCancel}
        disabled={saving}
        className="px-3 py-1.5 text-xs font-medium text-slate border border-stone rounded-lg hover:bg-mist transition-colors"
      >
        Cancel
      </button>
    </div>
  );
}

function InfoRow({ label, value, subtitle }: { label: string; value: string; subtitle?: string }) {
  return (
    <div className="flex items-start justify-between py-3 border-b border-stone last:border-b-0">
      <div className="flex-1 min-w-0">
        <span className="text-[13px] text-slate">{label}</span>
        {subtitle && (
          <p className="text-[11px] text-slate/70 leading-snug mt-0.5">{subtitle}</p>
        )}
      </div>
      <span className="text-[13px] font-semibold text-midnight shrink-0 ml-4 self-center">{value}</span>
    </div>
  );
}

function ColorPickerInput({ value, onChange, disabled, label, onReset }: {
  value: string;
  onChange: (hex: string) => void;
  disabled?: boolean;
  label: string;
  onReset?: () => void;
}) {
  const [inputValue, setInputValue] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const lastExternalValue = useRef(value);

  if (value !== lastExternalValue.current) {
    lastExternalValue.current = value;
    setInputValue(value);
    setError(null);
  }

  const handleInputChange = (val: string) => {
    let hex = val;
    if (!hex.startsWith('#')) hex = '#' + hex;
    setInputValue(hex);
    if (isValidHex(hex)) {
      setError(null);
      onChange(hex);
    } else if (hex.length > 1) {
      setError('Invalid hex code');
    }
  };

  return (
    <div>
      <label className="text-xs font-medium text-slate block mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={isValidHex(value) ? value : DEFAULT_BRAND_PRIMARY}
          onChange={e => {
            const hex = e.target.value.toUpperCase();
            setInputValue(hex);
            setError(null);
            onChange(hex);
          }}
          disabled={disabled}
          className="w-10 h-10 rounded-lg border border-stone cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 p-0.5"
        />
        <input
          type="text"
          value={inputValue}
          onChange={e => handleInputChange(e.target.value)}
          disabled={disabled}
          maxLength={7}
          className="w-24 px-2 py-2 text-sm text-midnight bg-white border border-stone rounded-lg focus:outline-none focus:ring-2 focus:ring-plum/40 disabled:bg-mist disabled:cursor-not-allowed font-mono"
          placeholder="#000000"
        />
        {onReset && !disabled && (
          <button
            type="button"
            onClick={onReset}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-mist transition-colors"
            title="Reset to optimal contrast color"
          >
            <RotateCcw size={14} className="text-slate" />
          </button>
        )}
      </div>
      {error && <p className="text-xs text-rose mt-1">{error}</p>}
    </div>
  );
}

// ── General Card ───────────────────────────────────────────────────────────

function GeneralCard({ org, onSave }: { org: Organization; onSave: (data: OrgSettingsUpdate) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [name, setName] = useState(org.name);
  const [logoBase64, setLogoBase64] = useState<string | null>(org.logoBase64 ?? null);
  const fileRef = useRef<HTMLInputElement>(null);

  function startEdit() {
    setName(org.name);
    setLogoBase64(org.logoBase64 ?? null);
    setError('');
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setError('');
  }

  function handleLogoFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setLogoBase64(reader.result as string);
    reader.readAsDataURL(file);
  }

  async function handleSave() {
    if (!name.trim()) { setError('Company name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({ name: name.trim(), logoBase64: logoBase64 ?? '' });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="px-4 py-3.5 border-b border-stone flex items-center justify-between">
        <div>
          <p className="font-semibold text-small text-midnight">General</p>
          <p className="text-[12px] text-slate leading-snug mt-0.5">
            Basic company identity visible across the app.
          </p>
        </div>
        <CardEditButton editing={editing} onEdit={startEdit} onSave={handleSave} onCancel={cancelEdit} saving={saving} />
      </div>

      <div className="px-4">
        {error && (
          <div className="mt-3 px-3 py-2 rounded-lg bg-rose/10 border border-rose/30 text-[12px] text-rose">
            {error}
          </div>
        )}

        {editing ? (
          <div className="py-4 flex flex-col gap-4">
            <div>
              <label htmlFor="cp-name" className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                Company Name <span className="text-rose">*</span>
              </label>
              <input
                id="cp-name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
              <p className="text-[11px] text-slate/70 mt-1 leading-snug">
                Displayed to other users, in the navigation bar, and in most communications with customers
              </p>
            </div>

            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-1">Logo</p>
              {logoBase64 ? (
                <div className="flex items-center gap-3">
                  <img src={logoBase64} alt="Logo" className="h-16 w-16 rounded-xl object-contain border border-stone bg-mist p-1" />
                  <button
                    type="button"
                    onClick={() => { setLogoBase64(null); if (fileRef.current) fileRef.current.value = ''; }}
                    className="text-[13px] text-slate hover:text-rose transition-colors"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center h-20 border-2 border-dashed border-stone rounded-xl cursor-pointer bg-mist hover:border-plum hover:bg-plum/5 transition-colors duration-150">
                  <Upload size={18} className="text-slate" />
                  <span className="text-[13px] text-slate mt-1">Upload PNG</span>
                  <input ref={fileRef} type="file" accept=".png,image/png" className="sr-only" onChange={handleLogoFile} />
                </label>
              )}
              <p className="text-[11px] text-slate/70 mt-1 leading-snug">
                Appears on customer-facing quotes and online pages
              </p>
            </div>
          </div>
        ) : (
          <>
            <InfoRow
              label="Company Name"
              value={org.name}
              subtitle="Displayed to other users, in the navigation bar, and in most communications with customers"
            />
            <div className="flex items-start justify-between py-3 border-b border-stone last:border-b-0">
              <div className="flex-1 min-w-0">
                <span className="text-[13px] text-slate">Logo</span>
                <p className="text-[11px] text-slate/70 leading-snug mt-0.5">
                  Appears on customer-facing quotes and online pages
                </p>
              </div>
              <div className="shrink-0 ml-4 self-center">
                {org.logoBase64 ? (
                  <img src={org.logoBase64} alt={org.name} className="h-10 w-10 rounded-lg object-contain border border-stone bg-mist p-0.5" />
                ) : (
                  <span className="text-[13px] font-semibold text-midnight">—</span>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

// ── Contact Card ───────────────────────────────────────────────────────────

function ContactCard({ org, onSave }: { org: Organization; onSave: (data: OrgSettingsUpdate) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const parsedPhone = useMemo(() => parseStoredPhone(org.phone), [org.phone]);

  const [billingEmail, setBillingEmail] = useState(org.billingEmail ?? '');
  const [businessAddress, setBusinessAddress] = useState<SelectedPlace | null>(org.businessAddress ?? null);
  const [phoneCountry, setPhoneCountry] = useState<CountryCode>(parsedPhone.country);
  const [phoneValue, setPhoneValue] = useState(parsedPhone.national);
  const [website, setWebsite] = useState(org.website ?? '');
  const [legalName, setLegalName] = useState(org.legalName ?? '');

  function startEdit() {
    const parsed = parseStoredPhone(org.phone);
    setBillingEmail(org.billingEmail ?? '');
    setBusinessAddress(org.businessAddress ?? null);
    setPhoneCountry(parsed.country);
    setPhoneValue(parsed.national);
    setWebsite(org.website ?? '');
    setLegalName(org.legalName ?? '');
    setError('');
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setError('');
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const phoneE164 = phoneValue ? phoneToE164(phoneValue, phoneCountry) : '';
      await onSave({
        billingEmail: billingEmail.trim(),
        businessAddress: businessAddress ?? null,
        phone: phoneE164,
        website: website.trim(),
        legalName: legalName.trim(),
      });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="px-4 py-3.5 border-b border-stone flex items-center justify-between">
        <div>
          <p className="font-semibold text-small text-midnight">Contact</p>
          <p className="text-[12px] text-slate leading-snug mt-0.5">
            How customers and partners can reach your company.
          </p>
        </div>
        <CardEditButton editing={editing} onEdit={startEdit} onSave={handleSave} onCancel={cancelEdit} saving={saving} />
      </div>

      <div className="px-4">
        {error && (
          <div className="mt-3 px-3 py-2 rounded-lg bg-rose/10 border border-rose/30 text-[12px] text-rose">
            {error}
          </div>
        )}

        {editing ? (
          <div className="py-4 flex flex-col gap-4">
            <div>
              <label htmlFor="cp-email" className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                Company Email
              </label>
              <input
                id="cp-email"
                type="email"
                value={billingEmail}
                onChange={e => setBillingEmail(e.target.value)}
                placeholder="billing@yourcompany.com"
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
              <p className="text-[11px] text-slate/70 mt-1 leading-snug">
                Shown on customer-facing quotes and communications
              </p>
            </div>

            <div>
              <AddressSearch
                id="cp-business-address"
                label="Business Address"
                value={businessAddress}
                onChange={setBusinessAddress}
              />
              <p className="text-[11px] text-slate/70 mt-1 leading-snug">
                Your company's physical location, shown on quotes and documents
              </p>
            </div>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                Phone Number
              </label>
              <PhoneInput
                id="cp-phone"
                country={phoneCountry}
                value={phoneValue}
                onCountryChange={setPhoneCountry}
                onValueChange={setPhoneValue}
              />
              <p className="text-[11px] text-slate/70 mt-1 leading-snug">
                Business phone number for customer inquiries
              </p>
            </div>

            <div>
              <label htmlFor="cp-website" className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                Website
              </label>
              <input
                id="cp-website"
                type="url"
                value={website}
                onChange={e => setWebsite(e.target.value)}
                placeholder="https://www.yourcompany.com"
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
              <p className="text-[11px] text-slate/70 mt-1 leading-snug">
                Displayed on customer-facing quotes and online pages
              </p>
            </div>

            <div>
              <label htmlFor="cp-legal-name" className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                Legal Company Name
              </label>
              <input
                id="cp-legal-name"
                type="text"
                value={legalName}
                onChange={e => setLegalName(e.target.value)}
                placeholder="e.g. Acme Moving LLC"
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
              <p className="text-[11px] text-slate/70 mt-1 leading-snug">
                Used on customer-facing quotes and for regulatory or payment requirements
              </p>
            </div>
          </div>
        ) : (
          <>
            <InfoRow
              label="Company Email"
              value={org.billingEmail || '—'}
              subtitle="Shown on customer-facing quotes and communications"
            />
            <InfoRow
              label="Business Address"
              value={org.businessAddress?.address || '—'}
              subtitle="Your company's physical location, shown on quotes and documents"
            />
            <InfoRow
              label="Phone"
              value={org.phone || '—'}
              subtitle="Business phone number for customer inquiries"
            />
            <InfoRow
              label="Website"
              value={org.website || '—'}
              subtitle="Displayed on customer-facing quotes and online pages"
            />
            <InfoRow
              label="Legal Company Name"
              value={org.legalName || '—'}
              subtitle="Used on customer-facing quotes and for regulatory or payment requirements"
            />
          </>
        )}
      </div>
    </section>
  );
}

// ── Brand Colors Card ──────────────────────────────────────────────────────

function BrandColorsCard({ org, orgId, refetch }: { org: Organization; orgId: string; refetch: () => void }) {
  const savedConfig = useMemo<{ brandPrimaryColor: string; brandTitleTextColor: string }>(() => ({
    brandPrimaryColor: org.bookingConfig?.brandPrimaryColor || DEFAULT_BRAND_PRIMARY,
    brandTitleTextColor: org.bookingConfig?.brandTitleTextColor || bestContrastColor(org.bookingConfig?.brandPrimaryColor || DEFAULT_BRAND_PRIMARY),
  }), [org.bookingConfig?.brandPrimaryColor, org.bookingConfig?.brandTitleTextColor]);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [brandConfig, setBrandConfig] = useState<{ brandPrimaryColor: string; brandTitleTextColor: string } | null>(null);

  const brandPrimaryColor = brandConfig?.brandPrimaryColor ?? savedConfig.brandPrimaryColor;
  const brandTitleTextColor = brandConfig?.brandTitleTextColor ?? savedConfig.brandTitleTextColor;

  function startEdit() {
    setBrandConfig({
      brandPrimaryColor: savedConfig.brandPrimaryColor,
      brandTitleTextColor: savedConfig.brandTitleTextColor,
    });
    setError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setBrandConfig(null);
    setError(null);
    setEditing(false);
  }

  async function handleSave() {
    if (!brandConfig) { setEditing(false); return; }
    setSaving(true);
    setError(null);
    try {
      await api.patch<{ success: boolean; data: Organization }>(
        `/org-admin/${orgId}/booking-config`,
        brandConfig,
      );
      await refetch();
      setBrandConfig(null);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="px-4 py-3.5 border-b border-stone flex items-center justify-between">
        <div>
          <p className="font-semibold text-small text-midnight">Brand Colors</p>
          <p className="text-[12px] text-slate leading-snug mt-0.5">
            Colors used on customer-facing pages and documents.
          </p>
        </div>
        <CardEditButton editing={editing} onEdit={startEdit} onSave={handleSave} onCancel={cancelEdit} saving={saving} />
      </div>

      <div className="px-4 py-4">
        <div className="space-y-4 lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0 lg:items-start">
          <div className="space-y-4">
            <ColorPickerInput
              value={brandPrimaryColor}
              onChange={hex => setBrandConfig(prev => {
                const newTextColor = bestContrastColor(hex);
                return { brandPrimaryColor: hex, brandTitleTextColor: prev?.brandTitleTextColor ?? newTextColor };
              })}
              disabled={!editing}
              label="Primary Brand Color"
            />

            <ColorPickerInput
              value={brandTitleTextColor}
              onChange={hex => setBrandConfig(prev => ({
                brandPrimaryColor: prev?.brandPrimaryColor ?? savedConfig.brandPrimaryColor,
                brandTitleTextColor: hex,
              }))}
              disabled={!editing}
              label="Title Text Color"
              onReset={editing ? () => {
                const optimal = bestContrastColor(brandPrimaryColor);
                setBrandConfig(prev => ({
                  brandPrimaryColor: prev?.brandPrimaryColor ?? savedConfig.brandPrimaryColor,
                  brandTitleTextColor: optimal,
                }));
              } : undefined}
            />

            {isValidHex(brandPrimaryColor) && isValidHex(brandTitleTextColor) && (
              <div className="flex items-center gap-2">
                <Info size={12} className="text-slate shrink-0" />
                <p className={`text-xs ${
                  contrastRatio(brandPrimaryColor, brandTitleTextColor) >= 4.5
                    ? 'text-green-600'
                    : contrastRatio(brandPrimaryColor, brandTitleTextColor) >= 3
                      ? 'text-amber-600'
                      : 'text-red-500'
                }`}>
                  Contrast ratio: {contrastRatio(brandPrimaryColor, brandTitleTextColor).toFixed(1)}:1
                  {contrastRatio(brandPrimaryColor, brandTitleTextColor) >= 4.5 ? ' (Good)' :
                   contrastRatio(brandPrimaryColor, brandTitleTextColor) >= 3 ? ' (Fair)' : ' (Poor — may be hard to read)'}
                </p>
              </div>
            )}
          </div>

          {/* Preview */}
          <div>
            <p className="text-xs font-medium text-slate uppercase tracking-wider mb-2">Preview</p>
            <div
              className="rounded-xl overflow-hidden border border-stone"
              style={{ backgroundColor: isValidHex(brandPrimaryColor) ? brandPrimaryColor : DEFAULT_BRAND_PRIMARY }}
            >
              <div className="flex items-center gap-3 p-4">
                {org.logoBase64 ? (
                  <img src={org.logoBase64} alt="" className="w-8 h-8 rounded-lg object-contain bg-white/20" />
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center font-bold text-sm"
                    style={{ color: isValidHex(brandTitleTextColor) ? brandTitleTextColor : DEFAULT_BRAND_TEXT }}
                  >
                    {(org.name ?? 'C')[0]}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: isValidHex(brandTitleTextColor) ? brandTitleTextColor : DEFAULT_BRAND_TEXT }}>
                    {org.name ?? 'Company Name'}
                  </p>
                  <p className="text-xs opacity-80" style={{ color: isValidHex(brandTitleTextColor) ? brandTitleTextColor : DEFAULT_BRAND_TEXT }}>
                    Moving Services
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && <p className="text-xs text-rose mt-3">{error}</p>}
      </div>
    </section>
  );
}

// ── Licenses Card ──────────────────────────────────────────────────────────

function LicensesCard({ org, onSave }: { org: Organization; onSave: (data: OrgSettingsUpdate) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [usdotNumber, setUsdotNumber] = useState(org.usdotNumber ?? '');
  const [mcNumber, setMcNumber] = useState(org.mcNumber ?? '');
  const [stateLicenses, setStateLicenses] = useState<StateLicense[]>(org.stateLicenses ?? []);

  function startEdit() {
    setUsdotNumber(org.usdotNumber ?? '');
    setMcNumber(org.mcNumber ?? '');
    setStateLicenses(org.stateLicenses ?? []);
    setError('');
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setError('');
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await onSave({
        usdotNumber: usdotNumber.trim(),
        mcNumber: mcNumber.trim(),
        stateLicenses: stateLicenses.filter(l => l.state.trim() && l.licenseNumber.trim()),
      });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="px-4 py-3.5 border-b border-stone flex items-center justify-between">
        <div>
          <p className="font-semibold text-small text-midnight">Licenses</p>
          <p className="text-[12px] text-slate leading-snug mt-0.5">
            Federal and state moving authority numbers.
          </p>
        </div>
        <CardEditButton editing={editing} onEdit={startEdit} onSave={handleSave} onCancel={cancelEdit} saving={saving} />
      </div>

      <div className="px-4">
        {error && (
          <div className="mt-3 px-3 py-2 rounded-lg bg-rose/10 border border-rose/30 text-[12px] text-rose">
            {error}
          </div>
        )}

        {editing ? (
          <div className="py-4 flex flex-col gap-4">
            <div>
              <label htmlFor="cp-usdot" className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                USDOT Number
              </label>
              <input
                id="cp-usdot"
                type="text"
                value={usdotNumber}
                onChange={e => setUsdotNumber(e.target.value)}
                placeholder="e.g. 1234567"
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
              <p className="text-[11px] text-slate/70 mt-1 leading-snug">
                Required for interstate moves in the U.S.
              </p>
            </div>

            <div>
              <label htmlFor="cp-mc" className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                MC Number
              </label>
              <input
                id="cp-mc"
                type="text"
                value={mcNumber}
                onChange={e => setMcNumber(e.target.value)}
                placeholder="e.g. MC-123456"
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
              <p className="text-[11px] text-slate/70 mt-1 leading-snug">
                Motor Carrier authority number for interstate moves
              </p>
            </div>

            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">
                State Mover Licenses
              </p>
              <p className="text-[11px] text-slate/70 -mt-1 mb-2 leading-snug">
                Add license numbers for each state you operate in
              </p>
              <div className="flex flex-col gap-2">
                {stateLicenses.map((license, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={license.state}
                      onChange={e => {
                        const updated = [...stateLicenses];
                        updated[idx] = { ...updated[idx], state: e.target.value.toUpperCase().slice(0, 2) };
                        setStateLicenses(updated);
                      }}
                      placeholder="ST"
                      maxLength={2}
                      className="w-16 h-11 px-3 rounded-lg border border-stone bg-white text-center
                                 text-small text-midnight placeholder:text-slate uppercase
                                 focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                 transition-colors duration-150"
                    />
                    <input
                      type="text"
                      value={license.licenseNumber}
                      onChange={e => {
                        const updated = [...stateLicenses];
                        updated[idx] = { ...updated[idx], licenseNumber: e.target.value };
                        setStateLicenses(updated);
                      }}
                      placeholder="License number"
                      className="flex-1 h-11 px-3 rounded-lg border border-stone bg-white
                                 text-small text-midnight placeholder:text-slate
                                 focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                 transition-colors duration-150"
                    />
                    <button
                      type="button"
                      onClick={() => setStateLicenses(stateLicenses.filter((_, i) => i !== idx))}
                      className="shrink-0 h-11 w-11 flex items-center justify-center rounded-lg
                                 border border-stone text-slate hover:text-rose hover:border-rose
                                 transition-colors duration-150"
                      aria-label="Remove license"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setStateLicenses([...stateLicenses, { state: '', licenseNumber: '' }])}
                  className="flex items-center justify-center gap-1.5 h-11 rounded-lg border border-dashed
                             border-stone text-[13px] text-slate font-semibold
                             hover:border-plum hover:text-plum active:bg-mist
                             transition-colors duration-150"
                >
                  <Plus size={15} />
                  Add State License
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <InfoRow
              label="USDOT Number"
              value={org.usdotNumber || '—'}
              subtitle="Required for interstate moves in the U.S."
            />
            <InfoRow
              label="MC Number"
              value={org.mcNumber || '—'}
              subtitle="Motor Carrier authority number for interstate moves"
            />
            <div className="flex items-start justify-between py-3 border-b border-stone last:border-b-0">
              <div className="flex-1 min-w-0">
                <span className="text-[13px] text-slate">State Mover Licenses</span>
                <p className="text-[11px] text-slate/70 leading-snug mt-0.5">
                  License numbers for each state you operate in
                </p>
              </div>
              <div className="shrink-0 ml-4 self-center text-right">
                {org.stateLicenses && org.stateLicenses.length > 0 ? (
                  <div className="flex flex-col gap-0.5">
                    {org.stateLicenses.map((l, i) => (
                      <span key={i} className="text-[13px] font-semibold text-midnight">
                        {l.state}: {l.licenseNumber}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-[13px] font-semibold text-midnight">—</span>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export function CompanyProfilePage() {
  const navigate = useNavigate();
  const { adminOrg, loading: profileLoading } = useUserProfile();
  const orgId = adminOrg?.orgId ?? '';
  const { org, loading, error, updateSettings, refetch } = useOrgSettings(orgId);

  if (profileLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!adminOrg) {
    return (
      <div className="flex flex-col items-center py-20 gap-3 text-center px-4">
        <p className="text-small text-slate">You don&apos;t have access to org admin settings.</p>
      </div>
    );
  }

  async function handleSave(data: OrgSettingsUpdate) {
    await updateSettings(data);
  }

  return (
    <div className="px-4 pt-6 pb-12 md:px-8 md:max-w-2xl lg:max-w-4xl md:mx-auto">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-small mb-4 -ml-1">
        <button
          type="button"
          onClick={() => navigate('/admin')}
          className="text-slate hover:text-midnight transition-colors duration-150"
        >
          Admin Panel
        </button>
        <ChevronRight size={14} className="text-stone" />
        <button
          type="button"
          onClick={() => navigate('/admin/company')}
          className="text-slate hover:text-midnight transition-colors duration-150"
        >
          Company
        </button>
        <ChevronRight size={14} className="text-stone" />
        <span className="text-midnight font-medium">Profile</span>
      </nav>

      {/* Org header */}
      {org && (
        <div className="flex items-center gap-4 mb-2">
          {org.logoBase64 ? (
            <img
              src={org.logoBase64}
              alt={org.name}
              className="shrink-0 w-14 h-14 rounded-xl object-contain border border-stone bg-mist p-1 md:w-20 md:h-20"
            />
          ) : (
            <div className="shrink-0 w-14 h-14 rounded-xl bg-plum/10 flex items-center justify-center md:w-20 md:h-20">
              <span className="font-display font-bold text-xl text-plum md:text-3xl">
                {org.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-display text-h2 text-midnight leading-tight truncate">{org.name}</p>
          </div>
        </div>
      )}

      <h1 className="font-display text-h1 text-midnight mb-1">Profile</h1>
      <p className="text-[13px] text-slate mb-6">
        Company name, logo, contact information, branding, and licenses.
      </p>

      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">Failed to load settings</p>
          <p className="text-small text-slate">{error}</p>
        </div>
      )}

      {!loading && !error && org && (
        <div className="flex flex-col gap-4">
          <GeneralCard org={org} onSave={handleSave} />
          <ContactCard org={org} onSave={handleSave} />
          <BrandColorsCard org={org} orgId={orgId} refetch={refetch} />
          <LicensesCard org={org} onSave={handleSave} />
        </div>
      )}
    </div>
  );
}
